/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback, useRef } from 'react'
import { css, Global } from '@emotion/react'
import {
  DownOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons'
import { Modal, App, Select, DatePicker, Tooltip } from 'antd'
const { Option } = Select
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import type { Venue } from '../hooks/useSchedulerLocations'
import type {
  TimeSlot,
  UpdateTimeSlotData,
} from '../hooks/useSchedulerTimeSlots'
import { DAYS_OF_WEEK, RECURRENCE_TYPES } from '../hooks/useSchedulerTimeSlots'
import TimePickerWithInterval from 'src/components/ts-components/TimePickerWithInterval'
import DayOfWeekSelector from './VenueCard/DayOfWeekSelector'
import type { TimeSlotEditState } from './VenueCard/types'
import { formatSlotDuration } from '../utils/timeRangeValidation'

const ALL_VALUE = 'all'
const LIST_VIEW_PICKER_CLASS = 'list-view-picker-popup'
const fullWidthStyle = { width: '100%' } as const
const darkDropdownStyle = { backgroundColor: '#212935' } as const
const iconFontStyle = { fontSize: 18 } as const

const iconButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.PRIMARY};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover {
    color: ${colors.PRIMARY_HOVER};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

interface DivisionOption {
  id: string
  name: string
  /** Full display name alias (API name_full) — legacy scope rows may store it. */
  nameFull?: string | null
}

interface TimeSlotsListViewProps {
  venues: Venue[]
  timeSlots: TimeSlot[]
  divisions: DivisionOption[]
  timeSlotsLoading: boolean
  onUpdateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  onDeleteTimeSlot: (timeSlotId: string) => Promise<void>
  onDuplicateTimeSlot: (timeSlotId: string) => Promise<TimeSlot>
}

interface FlattenedRow {
  id: string
  timeSlotId: string
  date: string
  sortDate: string
  time: string
  sortTime: string
  location: string
  surface: string
  division: string
  venueId: string
  facilityId: string
  divisionIds: string[]
}

const headerCellStyle = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
`

const dataCellStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
`

const editTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
  overflow: hidden;
  text-overflow: ellipsis;
`

const editTimePickerStyles = css`
  height: 36px;
  background-color: ${'#212935'};
  border: 1px solid ${'#313b4b'};
  border-radius: 8px;

  input {
    color: ${colors.NEUTRAL_0} !important;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
  }

  &:hover,
  &.ant-picker-focused {
    border-color: ${colors.PRIMARY};
  }

  .ant-picker-suffix {
    color: ${colors.NEUTRAL_50};
  }

  .ant-picker-clear {
    background: ${'#212935'};
    color: ${colors.NEUTRAL_50};
  }
`

const editSelectStyles = css`
  .ant-select-selector {
    background-color: ${'#212935'} !important;
    border: 1px solid ${'#313b4b'} !important;
    border-radius: 8px !important;
    height: 36px !important;
    overflow: hidden;
    padding: 2px 6px !important;
    color: ${colors.NEUTRAL_0} !important;
  }

  .ant-select-selection-item {
    line-height: 30px !important;
    color: ${colors.NEUTRAL_0} !important;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
  }

  .ant-select-selection-placeholder {
    color: ${colors.NEUTRAL_50} !important;
    line-height: 30px !important;
  }

  .ant-select-arrow {
    color: ${colors.NEUTRAL_0};
  }

  &.ant-select-multiple .ant-select-selection-item {
    background-color: ${colors.SECONDARY_300} !important;
    border: 1px solid ${'#313b4b'} !important;
    border-radius: 4px !important;
    color: ${colors.NEUTRAL_0} !important;
    height: 28px !important;
    line-height: 26px !important;
  }

  &.ant-select-multiple .ant-select-selection-item-remove {
    color: ${colors.NEUTRAL_50} !important;
    &:hover {
      color: ${colors.NEUTRAL_0} !important;
    }
  }

  &:hover .ant-select-selector {
    border-color: ${colors.PRIMARY} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.PRIMARY} !important;
    box-shadow: 0 0 0 2px ${colors.PRIMARY}33 !important;
  }

  .ant-select-clear {
    background: ${'#212935'};
    color: ${colors.NEUTRAL_50};
  }
`

/**
 * Display names for a slot's stored division scope. Post-fix rows store
 * canonical division UIDs (resolved via the lookup map); LEGACY rows may
 * still hold raw division NAMES — an unknown value renders AS-IS instead of
 * being dropped (which blanked the Division cell for every legacy row).
 * Empty scope = every division.
 */
export const displayDivisionNames = (
  divisionValues: string[] | null | undefined,
  divisionNameMap: Map<string, string>
): string => {
  if (!divisionValues || divisionValues.length === 0) {
    return 'All Divisions'
  }
  return divisionValues
    .map((value) => divisionNameMap.get(value) ?? value)
    .filter(Boolean)
    .join(', ')
}

/**
 * Whether a slot's stored division scope matches the division filter.
 * Matches the canonical UID and, for legacy rows, EVERY recognized display
 * alias of the division (`name` and `name_full` — historical rows stored
 * whichever the UI showed at the time). Unscoped rows (empty) match every
 * filter.
 */
export const rowMatchesDivisionFilter = (
  divisionValues: string[],
  divisionFilter: string,
  divisionAliasMap: Map<string, string[]>
): boolean => {
  if (divisionValues.length === 0) {
    return true
  }
  const acceptable = new Set<string>([
    divisionFilter,
    ...(divisionAliasMap.get(divisionFilter) ?? []),
  ])
  return divisionValues.some((value) => acceptable.has(value))
}

/**
 * Format days array to a readable string
 */
const formatDays = (days: number[]): string => {
  if (!days || days.length === 0) {
    return '-'
  }
  const sorted = [...days].sort((a, b) => a - b)

  // Check for common patterns
  const weekdays = [1, 2, 3, 4, 5]
  const weekends = [0, 6]
  const allDays = [0, 1, 2, 3, 4, 5, 6]

  if (sorted.length === 7 && sorted.every((d, i) => d === allDays[i])) {
    return 'Every Day'
  }
  if (sorted.length === 5 && sorted.every((d, i) => d === weekdays[i])) {
    return 'Weekdays'
  }
  if (sorted.length === 2 && sorted.every((d, i) => d === weekends[i])) {
    return 'Weekends'
  }

  return sorted
    .map((d) => DAYS_OF_WEEK.find((dw) => dw.value === d)?.short || '')
    .join(', ')
}

/**
 * Format time from "HH:MM" 24hr to "h:mm A"
 */
const formatTime = (time: string): string => {
  if (!time) {
    return '-'
  }
  const [h, m] = time.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const hr = h % 12 || 12
  return `${hr}:${m.toString().padStart(2, '0')} ${ampm}`
}

/**
 * Calculate duration between two HH:MM times
 */
const calculateDuration = (startTime: string, endTime: string): string =>
  formatSlotDuration(startTime, endTime, {
    withSeconds: true,
    invalidPlaceholder: '00:00:00',
  })

const TimeSlotsListView: React.FC<TimeSlotsListViewProps> = ({
  venues,
  timeSlots,
  divisions,
  timeSlotsLoading,
  onUpdateTimeSlot,
  onDeleteTimeSlot,
  onDuplicateTimeSlot,
}) => {
  const { message } = App.useApp()
  const divisionsSelectRef = useRef<any>(null)

  // Filter state
  const [divisionFilter, setDivisionFilter] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [surfaceFilter, setSurfaceFilter] = useState('')

  // Dropdown open states
  const [divisionDropdownOpen, setDivisionDropdownOpen] = useState(false)
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false)
  const [surfaceDropdownOpen, setSurfaceDropdownOpen] = useState(false)

  // Inline edit state
  const [editingSlotId, setEditingSlotId] = useState<string | null>(null)
  const [editState, setEditState] = useState<TimeSlotEditState | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)

  // Build venue/facility lookup
  const facilityMap = useMemo(() => {
    const map: Record<string, { venue: Venue; facilityName: string }> = {}
    for (const venue of venues) {
      for (const facility of venue.facilities || []) {
        map[facility.id] = { venue, facilityName: facility.name }
      }
    }
    return map
  }, [venues])

  // Get facilities for selected venue
  const selectedVenueFacilities = useMemo(() => {
    if (!locationFilter) {
      return []
    }
    const venue = venues.find((v) => v.id === locationFilter)
    return venue?.facilities || []
  }, [locationFilter, venues])

  // Build division name lookup for O(1) access
  const divisionNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const d of divisions) {
      map.set(d.id, d.name)
    }
    return map
  }, [divisions])

  // Every recognized alias per division (name + name_full) — legacy scope
  // rows stored whichever spelling the UI showed at the time.
  const divisionAliasMap = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const d of divisions) {
      const aliases = [d.name]
      if (d.nameFull && d.nameFull !== d.name) {
        aliases.push(d.nameFull)
      }
      map.set(d.id, aliases)
    }
    return map
  }, [divisions])

  // Flatten time slots into rows
  const flattenedRows = useMemo(() => {
    const rows: FlattenedRow[] = []
    for (const slot of timeSlots) {
      const info = facilityMap[slot.facility_id]
      if (!info) {
        continue
      }

      const divisionNames = displayDivisionNames(
        slot.divisions,
        divisionNameMap
      )

      // Each time range becomes a row
      if (slot.time_ranges && slot.time_ranges.length > 0) {
        for (const tr of slot.time_ranges) {
          const dateStr =
            slot.recurrence_type === 'specific' && slot.slot_date
              ? dayjs(slot.slot_date).format('ddd, MMM D, YYYY')
              : formatDays(slot.days)
          const sortDate =
            slot.recurrence_type === 'specific' && slot.slot_date
              ? slot.slot_date
              : [...(slot.days || [])].sort((a, b) => a - b).join(',') || ''

          rows.push({
            id: `${slot.id}-${tr.id}`,
            timeSlotId: slot.id,
            date: dateStr,
            sortDate,
            time: formatTime(tr.start_time),
            sortTime: tr.start_time,
            location: info.venue.name_full || info.venue.name,
            surface: info.facilityName,
            division: divisionNames,
            venueId: info.venue.id,
            facilityId: slot.facility_id,
            divisionIds: slot.divisions || [],
          })
        }
      } else {
        // Slot with no time ranges (shouldn't happen but handle it)
        const dateStr =
          slot.recurrence_type === 'specific' && slot.slot_date
            ? dayjs(slot.slot_date).format('ddd, MMM D, YYYY')
            : formatDays(slot.days)
        rows.push({
          id: slot.id,
          timeSlotId: slot.id,
          date: dateStr,
          sortDate: '',
          time: '-',
          sortTime: '',
          location: info.venue.name_full || info.venue.name,
          surface: info.facilityName,
          division: divisionNames,
          venueId: info.venue.id,
          facilityId: slot.facility_id,
          divisionIds: slot.divisions || [],
        })
      }
    }

    // Sort by date, then time
    rows.sort((a, b) => {
      const dc = a.sortDate.localeCompare(b.sortDate)
      if (dc !== 0) {
        return dc
      }
      return a.sortTime.localeCompare(b.sortTime)
    })

    return rows
  }, [timeSlots, facilityMap, divisionNameMap])

  // Apply filters
  const filteredRows = useMemo(() => {
    return flattenedRows.filter((row) => {
      if (
        divisionFilter &&
        !rowMatchesDivisionFilter(
          row.divisionIds,
          divisionFilter,
          divisionAliasMap
        )
      ) {
        return false
      }
      if (locationFilter && row.venueId !== locationFilter) {
        return false
      }
      if (surfaceFilter && row.facilityId !== surfaceFilter) {
        return false
      }
      return true
    })
  }, [
    flattenedRows,
    divisionFilter,
    locationFilter,
    surfaceFilter,
    divisionAliasMap,
  ])

  // Location options
  const locationOptions = useMemo(
    () => [
      { label: 'All Locations', value: ALL_VALUE },
      ...venues.map((v) => ({
        label: v.name_full || v.name,
        value: v.id,
      })),
    ],
    [venues]
  )

  // Surface options
  const surfaceOptions = useMemo(
    () => [
      { label: 'All Surfaces', value: ALL_VALUE },
      ...selectedVenueFacilities.map((f) => ({
        label: f.name,
        value: f.id,
      })),
    ],
    [selectedVenueFacilities]
  )

  // Division options
  const divisionOptions = useMemo(
    () => [
      { label: 'All Divisions', value: ALL_VALUE },
      ...divisions.map((d) => ({
        label: d.name,
        value: d.id,
      })),
    ],
    [divisions]
  )

  // Start editing a slot
  const handleEditSlot = useCallback(
    (row: FlattenedRow) => {
      const slot = timeSlots.find((s) => s.id === row.timeSlotId)
      if (!slot) {
        return
      }
      const timeRange = slot.time_ranges?.[0]
      setEditingSlotId(slot.id)
      setEditState({
        slotId: slot.id,
        frequency: slot.recurrence_type,
        days: slot.days || [],
        slotDate: slot.slot_date || null,
        startTime: timeRange?.start_time?.slice(0, 5) || '09:00',
        endTime: timeRange?.end_time?.slice(0, 5) || '17:00',
        divisions: slot.divisions || [],
      })
    },
    [timeSlots]
  )

  // Save edit
  const handleSave = useCallback(async () => {
    if (!editState || !editingSlotId) {
      return
    }
    setSaveLoading(true)
    try {
      const data: UpdateTimeSlotData = {
        recurrence_type: editState.frequency,
        days: editState.frequency === 'specific' ? [] : editState.days,
        time_ranges: [
          {
            start_time: editState.startTime,
            end_time: editState.endTime,
          },
        ],
        divisions: editState.divisions,
        slot_date:
          editState.frequency === 'specific'
            ? editState.slotDate || null
            : null,
      }
      await onUpdateTimeSlot(editingSlotId, data)
      message.success('Time slot updated')
      setEditingSlotId(null)
      setEditState(null)
    } catch {
      message.error('Failed to save time slot')
    } finally {
      setSaveLoading(false)
    }
  }, [editState, editingSlotId, onUpdateTimeSlot, message])

  // Cancel editing
  const handleCancel = useCallback(() => {
    setEditingSlotId(null)
    setEditState(null)
  }, [])

  // Duplicate slot
  const handleDuplicateClick = useCallback(
    async (row: FlattenedRow) => {
      try {
        await onDuplicateTimeSlot(row.timeSlotId)
        message.success('Time slot duplicated')
      } catch {
        message.error('Failed to duplicate time slot')
      }
    },
    [onDuplicateTimeSlot, message]
  )

  // Delete slot
  const handleDeleteClick = useCallback(
    (row: FlattenedRow) => {
      Modal.confirm({
        title: 'Delete Time Slot',
        content:
          'Are you sure you want to delete this time slot? This action cannot be undone.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        centered: true,
        onOk: async () => {
          try {
            await onDeleteTimeSlot(row.timeSlotId)
            message.success('Time slot deleted')
            // Clear edit state if we were editing this slot
            if (editingSlotId === row.timeSlotId) {
              setEditingSlotId(null)
              setEditState(null)
            }
          } catch {
            message.error('Failed to delete time slot')
          }
        },
      })
    },
    [onDeleteTimeSlot, message, editingSlotId]
  )

  // Find slot index for display number
  const slotIndexMap = useMemo(() => {
    const map = new Map<string, number>()
    timeSlots.forEach((slot, idx) => {
      map.set(slot.id, idx)
    })
    return map
  }, [timeSlots])

  // Render the inline edit row — self-contained block with its own header + controls
  const renderEditRow = (row: FlattenedRow) => {
    if (!editState) {
      return null
    }
    const slotIdx = slotIndexMap.get(row.timeSlotId) ?? 0
    const slotNumber = (slotIdx + 1).toString().padStart(4, '0')

    const editHeaderStyle = css`
      font-family: 'Inter', sans-serif;
      font-size: 12px;
      font-weight: 500;
      line-height: 17.4px;
      color: ${colors.NEUTRAL_70};
      text-transform: uppercase;
    `

    return (
      <div
        key={`edit-${row.timeSlotId}`}
        css={css`
          border-left: 3px solid ${colors.PRIMARY};
          background-color: ${'#212935'};
        `}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleCancel()
          }
        }}
      >
        {/* Edit header labels */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 2px 16px;
          `}
        >
          <span
            css={css`
              ${editHeaderStyle} width: 40px;
              flex-shrink: 0;
            `}
          >
            SLOT
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 144px;
              flex-shrink: 0;
            `}
          >
            FREQUENCY
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 220px;
              flex-shrink: 0;
            `}
          >
            DAYS OF WEEK
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 104px;
              flex-shrink: 0;
            `}
          >
            START TIME
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 104px;
              flex-shrink: 0;
            `}
          >
            END TIME
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 72px;
              flex-shrink: 0;
            `}
          >
            DURATION
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 120px;
              flex-shrink: 0;
            `}
          >
            LOCATION
          </span>
          <span
            css={css`
              ${editHeaderStyle} width: 100px;
              flex-shrink: 0;
            `}
          >
            SURFACE
          </span>
          <span
            css={css`
              ${editHeaderStyle} flex: 1;
              min-width: 120px;
            `}
          >
            DIVISIONS
          </span>
        </div>

        {/* Edit controls */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 16px;
          `}
        >
          {/* Slot number */}
          <span
            css={css`
              ${editTextStyles}
              width: 40px;
              color: ${colors.NEUTRAL_0};
              font-weight: 400;
              flex-shrink: 0;
            `}
          >
            {slotNumber}
          </span>

          {/* Frequency dropdown */}
          <Select
            value={editState.frequency}
            onChange={(value) => {
              if (value === 'specific') {
                setEditState({
                  ...editState,
                  frequency: value,
                  days: [],
                  slotDate: editState.slotDate || null,
                })
              } else {
                setEditState({
                  ...editState,
                  frequency: value,
                  slotDate: null,
                  days: editState.days.length > 0 ? editState.days : [1, 3, 5],
                })
              }
            }}
            disabled={saveLoading}
            css={editSelectStyles}
            style={{ width: 144 }} // eslint-disable-line react-native/no-inline-styles
            dropdownStyle={darkDropdownStyle}
            options={RECURRENCE_TYPES.map((rt) => ({
              value: rt.value,
              label: rt.label,
            }))}
          />

          {/* Days of week OR date picker */}
          <div
            css={css`
              width: 220px;
              flex-shrink: 0;
            `}
          >
            {editState.frequency === 'specific' ? (
              <DatePicker
                value={
                  editState.slotDate
                    ? dayjs(editState.slotDate, 'YYYY-MM-DD')
                    : null
                }
                onChange={(value) =>
                  setEditState({
                    ...editState,
                    slotDate: value ? value.format('YYYY-MM-DD') : null,
                  })
                }
                format='MMM D, YYYY'
                disabled={saveLoading}
                css={editTimePickerStyles}
                popupClassName={LIST_VIEW_PICKER_CLASS}
                style={fullWidthStyle}
                placeholder='Select date'
              />
            ) : (
              <DayOfWeekSelector
                selectedDays={editState.days}
                onChange={(days) => setEditState({ ...editState, days })}
                disabled={saveLoading}
              />
            )}
          </div>

          {/* Start time */}
          <div
            css={css`
              width: 104px;
              flex-shrink: 0;
            `}
          >
            <TimePickerWithInterval
              value={
                editState.startTime ? dayjs(editState.startTime, 'HH:mm') : null
              }
              onChange={(value) =>
                setEditState({
                  ...editState,
                  startTime: value
                    ? value.format('HH:mm')
                    : editState.startTime,
                })
              }
              disabled={saveLoading}
              style={fullWidthStyle}
            />
          </div>

          {/* End time */}
          <div
            css={css`
              width: 104px;
              flex-shrink: 0;
            `}
          >
            <TimePickerWithInterval
              value={
                editState.endTime ? dayjs(editState.endTime, 'HH:mm') : null
              }
              onChange={(value) =>
                setEditState({
                  ...editState,
                  endTime: value ? value.format('HH:mm') : editState.endTime,
                })
              }
              disabled={saveLoading}
              style={fullWidthStyle}
              relativeToTime={
                editState.startTime ? dayjs(editState.startTime, 'HH:mm') : null
              }
            />
          </div>

          {/* Duration (calculated) */}
          <span
            css={css`
              ${editTextStyles}
              width: 72px;
              flex-shrink: 0;
            `}
          >
            {calculateDuration(editState.startTime, editState.endTime)}
          </span>

          {/* Location (read-only) */}
          <span
            css={css`
              ${editTextStyles}
              width: 120px;
              flex-shrink: 0;
              color: ${colors.NEUTRAL_50};
            `}
            title={row.location}
          >
            {row.location}
          </span>

          {/* Surface (read-only) */}
          <span
            css={css`
              ${editTextStyles}
              width: 100px;
              flex-shrink: 0;
              color: ${colors.NEUTRAL_50};
            `}
            title={row.surface}
          >
            {row.surface}
          </span>

          {/* Divisions dropdown */}
          <div
            css={css`
              flex: 1;
              min-width: 120px;
            `}
          >
            <Select
              ref={divisionsSelectRef}
              mode='multiple'
              value={editState.divisions}
              onChange={(values: string[]) =>
                setEditState({ ...editState, divisions: values })
              }
              placeholder='All Divisions'
              disabled={saveLoading}
              css={editSelectStyles}
              style={fullWidthStyle}
              maxTagCount='responsive'
              allowClear
              popupClassName='dark-select-popup'
              // eslint-disable-next-line react/no-unstable-nested-components
              dropdownRender={(menu) => (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      cursor: pointer;
                      color: ${colors.PRIMARY};
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      &:hover {
                        background-color: ${colors.SECONDARY_400};
                      }
                    `}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const allIds = divisions.map((d) => d.id)
                      const allSelected =
                        editState.divisions.length === divisions.length
                      setEditState({
                        ...editState,
                        divisions: allSelected ? [] : allIds,
                      })
                      if (!allSelected) {
                        divisionsSelectRef.current?.blur()
                      }
                    }}
                  >
                    All Divisions
                  </div>
                  {menu}
                </>
              )}
            >
              {divisions.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Actions: Save + Cancel */}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              flex-shrink: 0;
            `}
          >
            <button
              type='button'
              onClick={handleSave}
              disabled={saveLoading}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 16px;
                background: ${colors.PRIMARY};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.2s ease;

                &:hover:not(:disabled) {
                  background: ${colors.PRIMARY_HOVER};
                }

                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              <SaveOutlined
                style={{ fontSize: 16, color: colors.SECONDARY_300 }} // eslint-disable-line react-native/no-inline-styles
              />
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.SECONDARY_300};
                `}
              >
                Save
              </span>
            </button>
            <Tooltip title='Cancel' placement='top'>
              <button
                type='button'
                onClick={handleCancel}
                disabled={saveLoading}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 32px;
                  height: 32px;
                  background: transparent;
                  border: 1px solid ${colors.NEUTRAL_50};
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.2s ease;

                  &:hover:not(:disabled) {
                    border-color: ${colors.ERROR_200};
                    background: ${colors.ERROR_200}20;
                  }

                  &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }
                `}
              >
                <CloseOutlined
                  style={{ fontSize: 14, color: colors.NEUTRAL_50 }} // eslint-disable-line react-native/no-inline-styles
                />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    )
  }

  // Build the visible row list — collapse all rows for an editing slot into one edit row
  const renderRows = () => {
    const rendered: React.ReactNode[] = []
    const editSlotRendered = new Set<string>()
    let displayIdx = 0

    for (const row of filteredRows) {
      // If this row's slot is being edited
      if (editingSlotId && row.timeSlotId === editingSlotId) {
        // Only render the edit row once per slot
        if (!editSlotRendered.has(row.timeSlotId)) {
          editSlotRendered.add(row.timeSlotId)
          rendered.push(renderEditRow(row))
        }
        continue
      }

      // Normal display row
      const idx = displayIdx++
      rendered.push(
        <div
          key={row.id}
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 16px;
            background: ${idx % 2 === 0
              ? colors.SECONDARY_500
              : colors.SECONDARY_400};
            transition: filter 0.15s ease;
            cursor: pointer;

            &:hover {
              filter: brightness(1.15);
            }

            &:hover .list-row-actions {
              opacity: 1;
            }
          `}
          onClick={() => handleEditSlot(row)}
        >
          <span
            css={css`
              ${dataCellStyle}
              width: 140px;
              height: 20px;
              flex-shrink: 0;
            `}
          >
            {row.date}
          </span>
          <span
            css={css`
              ${dataCellStyle}
              width: 80px;
              flex-shrink: 0;
            `}
          >
            {row.time}
          </span>
          <span
            css={css`
              ${dataCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            {row.location}
          </span>
          <span
            css={css`
              ${dataCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            {row.surface}
          </span>
          <span
            css={css`
              ${dataCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            {row.division}
          </span>
          <div
            className='list-row-actions'
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
              opacity: 0;
              transition: opacity 0.15s ease;
              flex-shrink: 0;
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title='Edit Time Slot' placement='top'>
              <button
                type='button'
                onClick={() => handleEditSlot(row)}
                css={iconButtonStyles}
              >
                <EditOutlined style={iconFontStyle} />
              </button>
            </Tooltip>
            <Tooltip title='Duplicate Time Slot' placement='top'>
              <button
                type='button'
                onClick={() => handleDuplicateClick(row)}
                css={iconButtonStyles}
              >
                <CopyOutlined style={iconFontStyle} />
              </button>
            </Tooltip>
            <Tooltip title='Delete Time Slot' placement='top'>
              <button
                type='button'
                onClick={() => handleDeleteClick(row)}
                css={css`
                  ${iconButtonStyles}
                  color: ${colors.ERROR_200};

                  &:hover {
                    color: ${colors.ERROR_300};
                  }
                `}
              >
                <DeleteOutlined style={iconFontStyle} />
              </button>
            </Tooltip>
          </div>
        </div>
      )
    }

    return rendered
  }

  return (
    <div>
      {/* Global styles for picker popups */}
      <Global
        styles={css`
          .dark-select-popup.ant-select-dropdown,
          .dark-select-popup .ant-select-dropdown {
            background-color: ${colors.SECONDARY_400} !important;
            border: 1px solid ${colors.SECONDARY_200} !important;
          }

          .dark-select-popup .ant-select-item,
          .dark-select-popup .ant-select-item-option-content {
            color: ${colors.WHITE} !important;
          }

          .dark-select-popup
            .ant-select-item-option-active:not(
              .ant-select-item-option-disabled
            ) {
            background-color: ${colors.SECONDARY_300} !important;
          }

          .dark-select-popup
            .ant-select-item-option-selected:not(
              .ant-select-item-option-disabled
            ) {
            background-color: ${colors.PRIMARY} !important;
          }

          .dark-select-popup
            .ant-select-item-option-selected:not(
              .ant-select-item-option-disabled
            )
            .ant-select-item-option-content {
            color: ${colors.SECONDARY_500} !important;
          }

          .${LIST_VIEW_PICKER_CLASS} {
            .ant-picker-panel-container {
              background-color: ${colors.SECONDARY_300};
              border: 1px solid ${colors.SECONDARY_200};
              border-radius: 8px;
              overflow: hidden;
            }

            .ant-picker-panel {
              background-color: ${colors.SECONDARY_300};
              border: none;
            }

            .ant-picker-header {
              color: ${colors.WHITE};
              border-bottom: 1px solid ${colors.SECONDARY_200};
            }

            .ant-picker-cell {
              color: ${colors.NEUTRAL_50};
            }

            .ant-picker-cell-in-view {
              color: ${colors.NEUTRAL_0};
            }

            .ant-picker-cell:hover:not(.ant-picker-cell-selected)
              .ant-picker-cell-inner {
              background-color: ${colors.SECONDARY_400} !important;
            }

            .ant-picker-cell-selected .ant-picker-cell-inner {
              background-color: ${colors.PRIMARY} !important;
              color: ${colors.PRIMARY_TEXT} !important;
            }

            .ant-picker-cell-today .ant-picker-cell-inner::before {
              border-color: ${colors.PRIMARY} !important;
            }

            .ant-picker-header button {
              color: ${colors.NEUTRAL_50};

              &:hover {
                color: ${colors.NEUTRAL_0};
              }
            }

            .ant-picker-super-prev-icon,
            .ant-picker-prev-icon,
            .ant-picker-next-icon,
            .ant-picker-super-next-icon {
              color: ${colors.NEUTRAL_50};
            }
          }
        `}
      />

      {/* Filter Bar */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          align-items: flex-end;
          margin-bottom: 16px;
        `}
      >
        {/* Division filter */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
            min-width: 0;
          `}
        >
          <SelectInput
            label='Division'
            disabled={timeSlotsLoading}
            options={divisionOptions}
            name='division'
            value={divisionFilter || ALL_VALUE}
            onChange={(value) =>
              setDivisionFilter(value === ALL_VALUE ? '' : value)
            }
            fieldErrors={{}}
            customError={null}
            showSearch={false}
            backgroundColor={colors.SECONDARY_400}
            allowClear={false}
            open={divisionDropdownOpen}
            onDropdownVisibleChange={setDivisionDropdownOpen}
            suffixIcon={
              <DownOutlined
                style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }} // eslint-disable-line react-native/no-inline-styles
                onClick={() => setDivisionDropdownOpen((prev) => !prev)}
              />
            }
          />
        </div>

        {/* Location filter */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
            min-width: 0;
          `}
        >
          <SelectInput
            label='Location'
            disabled={timeSlotsLoading}
            options={locationOptions}
            name='location'
            value={locationFilter || ALL_VALUE}
            onChange={(value) => {
              setLocationFilter(value === ALL_VALUE ? '' : value)
              setSurfaceFilter('')
            }}
            fieldErrors={{}}
            customError={null}
            showSearch={true}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            backgroundColor={colors.SECONDARY_400}
            allowClear={false}
            open={locationDropdownOpen}
            onDropdownVisibleChange={setLocationDropdownOpen}
            suffixIcon={
              <DownOutlined
                style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }} // eslint-disable-line react-native/no-inline-styles
                onClick={() => setLocationDropdownOpen((prev) => !prev)}
              />
            }
          />
        </div>

        {/* Surface filter */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
            min-width: 0;
          `}
        >
          <SelectInput
            label='Surface'
            disabled={timeSlotsLoading || !locationFilter}
            options={surfaceOptions}
            name='surface'
            value={surfaceFilter || ALL_VALUE}
            onChange={(value) =>
              setSurfaceFilter(value === ALL_VALUE ? '' : value)
            }
            fieldErrors={{}}
            customError={null}
            showSearch={false}
            backgroundColor={colors.SECONDARY_400}
            allowClear={false}
            open={surfaceDropdownOpen}
            onDropdownVisibleChange={setSurfaceDropdownOpen}
            suffixIcon={
              <DownOutlined
                style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }} // eslint-disable-line react-native/no-inline-styles
                onClick={() => setSurfaceDropdownOpen((prev) => !prev)}
              />
            }
          />
        </div>
      </div>

      {/* Table */}
      <div
        css={css`
          border: 1px solid ${colors.SECONDARY_200};
          border-radius: 8px;
          overflow: hidden;
        `}
      >
        {/* Header row */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 4px 16px;
            background: ${colors.SECONDARY_300};
          `}
        >
          <span
            css={css`
              ${headerCellStyle}
              width: 140px;
              flex-shrink: 0;
            `}
          >
            DATE
          </span>
          <span
            css={css`
              ${headerCellStyle}
              width: 80px;
              flex-shrink: 0;
            `}
          >
            TIME
          </span>
          <span
            css={css`
              ${headerCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            LOCATION
          </span>
          <span
            css={css`
              ${headerCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            SURFACE
          </span>
          <span
            css={css`
              ${headerCellStyle}
              flex: 1;
              min-width: 0;
            `}
          >
            DIVISIONS
          </span>
          {/* Spacer for action icons (3 icons × 24px + 2 gaps × 16px = 104px) */}
          <div
            css={css`
              width: 104px;
              flex-shrink: 0;
            `}
          />
        </div>

        {/* Data rows */}
        {filteredRows.length === 0 ? (
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 32px 16px;
              color: ${colors.NEUTRAL_50};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
            `}
          >
            {timeSlotsLoading ? 'Loading time slots...' : 'No time slots found'}
          </div>
        ) : (
          renderRows()
        )}
      </div>
    </div>
  )
}

export default TimeSlotsListView
